import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Upload, Trash2, Image, Loader2, ChefHat, Plus, Edit, Check, X, MessageSquare, Download, ThumbsUp, ThumbsDown, Tag } from 'lucide-react';
import { Input } from '../components/ui/input';

const SettingsPage = () => {
    const { getAuthHeaders, API, isAdmin } = useAuth();
    const [logo, setLogo] = useState(null);
    const [logoFilename, setLogoFilename] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [newCommentType, setNewCommentType] = useState('general');
    const [editingComment, setEditingComment] = useState(null);
    const [editLabel, setEditLabel] = useState('');
    const [editType, setEditType] = useState('general');
    const [commentFilter, setCommentFilter] = useState('all');
    const [uploadingComments, setUploadingComments] = useState(false);
    const commentFileRef = useRef(null);

    useEffect(() => { fetchLogo(); fetchComments(); }, []);

    const fetchComments = async () => {
        try {
            const res = await axios.get(`${API}/admin/attendance-comments`, { headers: getAuthHeaders() });
            setComments(res.data);
        } catch (e) { console.error(e); }
    };

    const addComment = async () => {
        if (!newComment.trim()) return;
        try {
            await axios.post(`${API}/admin/attendance-comments`, { label: newComment.trim(), type: newCommentType }, { headers: getAuthHeaders() });
            setNewComment('');
            fetchComments();
            toast.success('Comment added');
        } catch (e) { toast.error('Failed to add comment'); }
    };

    const saveEditComment = async (id) => {
        if (!editLabel.trim()) return;
        try {
            await axios.put(`${API}/admin/attendance-comments/${id}`, { label: editLabel.trim(), type: editType }, { headers: getAuthHeaders() });
            setEditingComment(null);
            fetchComments();
            toast.success('Comment updated');
        } catch (e) { toast.error('Failed to update'); }
    };

    const deleteComment = async (id) => {
        try {
            await axios.delete(`${API}/admin/attendance-comments/${id}`, { headers: getAuthHeaders() });
            fetchComments();
            toast.success('Comment deleted');
        } catch (e) { toast.error('Failed to delete'); }
    };

    const handleCommentsUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingComments(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const res = await axios.post(`${API}/admin/attendance-comments/upload`, fd, {
                headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
            });
            toast.success(res.data.message);
            fetchComments();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Upload failed');
        } finally {
            setUploadingComments(false);
            if (commentFileRef.current) commentFileRef.current.value = '';
        }
    };

    const downloadCommentsTemplate = async () => {
        try {
            const res = await axios.get(`${API}/admin/attendance-comments/template`, { headers: getAuthHeaders(), responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a'); link.href = url;
            link.setAttribute('download', 'attendance_comments_template.xlsx');
            document.body.appendChild(link); link.click(); link.remove();
        } catch (e) { toast.error('Failed'); }
    };

    const fetchLogo = async () => {
        try {
            const response = await axios.get(`${API}/settings/logo`);
            if (response.data.logo) { setLogo(response.data.logo); setLogoFilename(response.data.filename); }
        } catch (error) { console.error('Failed to fetch logo:', error); }
        finally { setLoading(false); }
    };

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
        if (file.size > 2 * 1024 * 1024) { toast.error('File must be under 2MB'); return; }
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            await axios.post(`${API}/admin/logo`, formData, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
            toast.success('Logo uploaded successfully');
            fetchLogo();
        } catch (error) { toast.error(error.response?.data?.detail || 'Failed to upload logo'); }
        finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
    };

    const handleDelete = async () => {
        if (!window.confirm('Remove the custom logo?')) return;
        try { await axios.delete(`${API}/admin/logo`, { headers: getAuthHeaders() }); setLogo(null); setLogoFilename(null); toast.success('Logo removed'); }
        catch (error) { toast.error('Failed to remove logo'); }
    };

    if (!isAdmin()) {
        return (<Layout><div className="text-center py-16 bg-white rounded-lg border border-gray-200"><Image className="w-10 h-10 mx-auto mb-3 text-gray-400 opacity-40" /><h3 className="text-base font-semibold text-gray-700 mb-1">Admin Access Required</h3></div></Layout>);
    }

    const filteredComments = commentFilter === 'all' ? comments : comments.filter(c => (c.type || 'general') === commentFilter);
    const meritCount = comments.filter(c => c.type === 'merit').length;
    const demeritCount = comments.filter(c => c.type === 'demerit').length;
    const generalCount = comments.filter(c => !c.type || c.type === 'general').length;

    const getTypeIcon = (type) => {
        if (type === 'merit') return <ThumbsUp className="w-3.5 h-3.5 text-green-600" />;
        if (type === 'demerit') return <ThumbsDown className="w-3.5 h-3.5 text-red-500" />;
        return <Tag className="w-3.5 h-3.5 text-gray-400" />;
    };

    const getTypeBadge = (type) => {
        if (type === 'merit') return 'bg-green-50 text-green-700 border-green-200';
        if (type === 'demerit') return 'bg-red-50 text-red-600 border-red-200';
        return 'bg-gray-50 text-gray-600 border-gray-200';
    };

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in" data-testid="settings-page">
                <div>
                    <h1 className="font-heading text-3xl font-bold text-ica-navy">Settings</h1>
                    <p className="text-gray-500 mt-1">Manage school branding and configuration</p>
                </div>

                {/* Logo Section */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-ica-navy mb-4">School Logo</h2>
                    <p className="text-sm text-gray-500 mb-6">Upload your school logo. It will appear in the sidebar and login page.</p>
                    <div className="flex items-start gap-8">
                        <div className="flex flex-col items-center gap-3">
                            <div className="w-32 h-32 border-2 border-dashed border-gray-200 rounded-xl flex items-center justify-center bg-gray-50 overflow-hidden">
                                {loading ? <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ica-navy" /> : logo ? <img src={logo} alt="School logo" className="w-full h-full object-contain p-2" data-testid="logo-preview" /> : <div className="text-center"><ChefHat className="w-12 h-12 text-gray-300 mx-auto" /><p className="text-xs text-gray-400 mt-1">Default</p></div>}
                            </div>
                            {logoFilename && <p className="text-xs text-gray-400">{logoFilename}</p>}
                        </div>
                        <div className="flex-1 space-y-4">
                            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" id="logo-upload" data-testid="logo-file-input" />
                            <div className="flex items-center gap-3">
                                <label htmlFor="logo-upload">
                                    <Button asChild disabled={uploading} className="cursor-pointer">
                                        <span data-testid="upload-logo-btn">{uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</> : <><Upload className="w-4 h-4 mr-2" />{logo ? 'Replace Logo' : 'Upload Logo'}</>}</span>
                                    </Button>
                                </label>
                                {logo && <Button variant="outline" onClick={handleDelete} className="text-red-500 hover:text-red-600 hover:bg-red-50 border-red-200" data-testid="remove-logo-btn"><Trash2 className="w-4 h-4 mr-2" />Remove</Button>}
                            </div>
                            <div className="text-sm text-gray-500 space-y-1"><p>Accepted: PNG, JPG, SVG, WebP</p><p>Max size: 2MB</p></div>
                        </div>
                    </div>
                </div>

                {/* Attendance Comments with Merits/Demerits */}
                <div className="bg-white rounded-lg border border-gray-200 p-6" data-testid="attendance-comments-section">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <MessageSquare className="w-5 h-5 text-ica-navy" />
                            <h2 className="text-lg font-semibold text-ica-navy">Attendance Comments</h2>
                        </div>
                        <div className="flex gap-2">
                            <input ref={commentFileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleCommentsUpload} className="hidden" id="comments-upload" />
                            <label htmlFor="comments-upload">
                                <Button asChild variant="outline" size="sm" disabled={uploadingComments} className="cursor-pointer">
                                    <span data-testid="upload-comments-btn">{uploadingComments ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Uploading...</> : <><Upload className="w-3.5 h-3.5 mr-1" />Upload</>}</span>
                                </Button>
                            </label>
                            <Button variant="outline" size="sm" onClick={downloadCommentsTemplate} data-testid="download-comments-template"><Download className="w-3.5 h-3.5 mr-1" />Template</Button>
                        </div>
                    </div>
                    <p className="text-sm text-gray-500 mb-4">Predefined comments grouped into Merits (positive) and Demerits (negative). Upload via Excel or add manually.</p>

                    {/* Add new comment */}
                    <div className="flex gap-2 mb-4">
                        <Input value={newComment} onChange={e => setNewComment(e.target.value)} placeholder="New comment..." className="flex-1" data-testid="new-comment-input"
                            onKeyDown={e => { if (e.key === 'Enter') addComment(); }} />
                        <Select value={newCommentType} onValueChange={setNewCommentType}>
                            <SelectTrigger className="w-[130px]" data-testid="new-comment-type">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="merit"><span className="flex items-center gap-1"><ThumbsUp className="w-3.5 h-3.5 text-green-600" /> Merit</span></SelectItem>
                                <SelectItem value="demerit"><span className="flex items-center gap-1"><ThumbsDown className="w-3.5 h-3.5 text-red-500" /> Demerit</span></SelectItem>
                                <SelectItem value="general"><span className="flex items-center gap-1"><Tag className="w-3.5 h-3.5 text-gray-400" /> General</span></SelectItem>
                            </SelectContent>
                        </Select>
                        <Button onClick={addComment} data-testid="add-comment-btn"><Plus className="w-4 h-4 mr-1" />Add</Button>
                    </div>

                    {/* Filter tabs */}
                    <div className="flex gap-1.5 mb-4">
                        {[
                            { id: 'all', label: `All (${comments.length})` },
                            { id: 'merit', label: `Merits (${meritCount})`, color: 'text-green-700' },
                            { id: 'demerit', label: `Demerits (${demeritCount})`, color: 'text-red-600' },
                            { id: 'general', label: `General (${generalCount})` }
                        ].map(f => (
                            <button key={f.id} onClick={() => setCommentFilter(f.id)}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${commentFilter === f.id ? 'bg-ica-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                                {f.label}
                            </button>
                        ))}
                    </div>

                    {/* Comments List */}
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                        {filteredComments.map(c => (
                            <div key={c.id} className={`flex items-center justify-between px-4 py-2.5 rounded-lg border ${getTypeBadge(c.type || 'general')}`} data-testid={`comment-item-${c.id}`}>
                                {editingComment === c.id ? (
                                    <div className="flex items-center gap-2 flex-1 mr-2">
                                        <Input value={editLabel} onChange={e => setEditLabel(e.target.value)} className="h-8 text-sm flex-1" autoFocus
                                            onKeyDown={e => { if (e.key === 'Enter') saveEditComment(c.id); if (e.key === 'Escape') setEditingComment(null); }} />
                                        <Select value={editType} onValueChange={setEditType}>
                                            <SelectTrigger className="w-[110px] h-8 text-xs"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="merit">Merit</SelectItem>
                                                <SelectItem value="demerit">Demerit</SelectItem>
                                                <SelectItem value="general">General</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <Button size="sm" variant="ghost" onClick={() => saveEditComment(c.id)} className="h-8 w-8 p-0 text-emerald-500 hover:text-emerald-600 dark:text-emerald-400" title="Save"><Check className="w-4 h-4" /></Button>
                                        <Button size="sm" variant="ghost" onClick={() => setEditingComment(null)} className="h-8 w-8 p-0 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300" title="Cancel"><X className="w-4 h-4" /></Button>
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex items-center gap-2">
                                            {getTypeIcon(c.type || 'general')}
                                            <span className="text-sm">{c.label}</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-500 hover:text-ica-navy dark:text-slate-400 dark:hover:text-ica-gold"
                                                onClick={() => { setEditingComment(c.id); setEditLabel(c.label); setEditType(c.type || 'general'); }} data-testid={`edit-comment-${c.id}`} title="Edit Comment">
                                                <Edit className="w-3.5 h-3.5" />
                                            </Button>
                                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300"
                                                onClick={() => deleteComment(c.id)} data-testid={`delete-comment-${c.id}`} title="Delete Comment">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                        {filteredComments.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No comments{commentFilter !== 'all' ? ` in this category` : ' yet'}</p>}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default SettingsPage;
